import { Field } from '@base-ui/react/field';
import { Input, InputProps } from '@base-ui/react/input';
import { BaseUIEvent } from '@base-ui/react/types';
import { cn } from '@foundation/utils/cn';
import { ReactNode, SyntheticEvent } from 'react';

import { Warning } from '@design-system/icons';

// Abstracts Base UI's event type so consumers don't need to depend on @base-ui/react directly.
export type TextInputEvent<E extends SyntheticEvent<Element, Event>> =
  BaseUIEvent<E>;

export type HeadlessTextInputStyles = {
  readonly root?: string;
  readonly label?: string;
  readonly requiredIndicator?: string;
  readonly control?: string;
  readonly startSlot?: string;
  readonly endSlot?: string;
  readonly clearButton?: string;
  readonly helperText?: string;
  readonly error?: string;
};

export type HeadlessTextInputProps = Omit<InputProps, 'className'> & {
  readonly label?: ReactNode;
  readonly error?: ReactNode;
  readonly helperText?: ReactNode;
  readonly success?: boolean;
  readonly isLoading?: boolean;
  readonly clearable?: boolean;
  readonly onClear?: () => void;
  readonly startSlot?: ReactNode;
  readonly endSlot?: ReactNode;
  readonly renderLoadingIndicator?: () => ReactNode;
  readonly renderClearIcon?: () => ReactNode;
  readonly renderSuccessIndicator?: () => ReactNode;
  readonly showRequired?: boolean;
  readonly styles?: HeadlessTextInputStyles;
};

export function HeadlessTextInput({
  label,
  error,
  helperText,
  success = false,
  isLoading = false,
  clearable = false,
  onClear,
  startSlot,
  endSlot,
  renderLoadingIndicator,
  renderClearIcon,
  renderSuccessIndicator,
  showRequired = false,
  required,
  disabled,
  readOnly,
  styles = {},
  type = 'text',
  ...inputProps
}: HeadlessTextInputProps) {
  // Precedence for what occupies the end slot: loading > clear > success > caller-provided adornment.
  const hasValue = Boolean(inputProps.value ?? inputProps.defaultValue);
  const showClearButton =
    clearable &&
    Boolean(onClear) &&
    !disabled &&
    !readOnly &&
    !isLoading &&
    hasValue;
  const showSuccessIndicator =
    success && !error && !isLoading && !showClearButton;

  const resolvedEndSlot = isLoading ? (
    renderLoadingIndicator?.()
  ) : showClearButton ? (
    <button
      type="button"
      aria-label="Clear"
      onClick={onClear}
      className={cn('cursor-pointer', styles.clearButton)}
    >
      {renderClearIcon?.()}
    </button>
  ) : showSuccessIndicator ? (
    renderSuccessIndicator?.()
  ) : (
    endSlot
  );

  return (
    <Field.Root
      className={cn('flex w-full flex-col gap-1', styles.root)}
      disabled={disabled}
      invalid={Boolean(error)}
    >
      <div className="relative flex items-center">
        {startSlot && (
          <div
            className={cn(
              'absolute left-4 top-1/2 flex -translate-y-1/2 items-center',
              styles.startSlot,
            )}
          >
            {startSlot}
          </div>
        )}

        {label && (
          <Field.Label
            className={cn(
              'pointer-events-none absolute left-4 top-1/2 -translate-y-1/2',
              'data-filled:top-2 data-filled:translate-y-0',
              'data-focused:top-2 data-focused:translate-y-0',
              styles.label,
            )}
          >
            {label}
            {required && showRequired && (
              <span
                aria-hidden="true"
                className={styles.requiredIndicator}
              >
                &nbsp;*
              </span>
            )}
          </Field.Label>
        )}

        <Input
          type={type}
          required={required}
          disabled={disabled}
          readOnly={readOnly}
          data-success={success || undefined}
          className={cn('w-full', styles.control)}
          {...inputProps}
        />

        {resolvedEndSlot && (
          <div
            className={cn(
              'absolute right-3 top-1/2 flex -translate-y-1/2 items-center',
              styles.endSlot,
            )}
          >
            {resolvedEndSlot}
          </div>
        )}
      </div>

      {error ? (
        <Field.Error
          match
          className={styles.error}
        >
          <div className="flex gap-1">
            <Warning size={20} />
            {error}
          </div>
        </Field.Error>
      ) : (
        helperText && (
          <Field.Description className={styles.helperText}>
            {helperText}
          </Field.Description>
        )
      )}
    </Field.Root>
  );
}
